import { DirectAnswersId } from "./DirectAnswers";
import { InDirectAnswersId } from "./InDirectAnswers";

export type ApkSurveyModel = Record<DirectAnswersId | InDirectAnswersId | string, boolean>;
export type OldAnswerModel = Record<string, boolean | null> | null;
export interface NewAnswerModel {
  answerUuid: string;
}

export class ApkSurvey {
  private _survey: ApkSurveyModel;
  private _isDirectSurvey: boolean;

  constructor(answersModel: NewAnswerModel[], isDirectSurvey: boolean) {
    this._isDirectSurvey = isDirectSurvey;
    this._survey = this.buildSurveyFromNewAnswersModel(answersModel);
  }

  private buildSurveyFromNewAnswersModel(answersModel: NewAnswerModel[]): ApkSurveyModel {
    const apkSurvey: ApkSurveyModel = {} as ApkSurveyModel;

    if (this._isDirectSurvey) {
      Object.values(DirectAnswersId).forEach((questionId) => {
        apkSurvey[questionId] = answersModel.some((answer) => answer.answerUuid === questionId);
      });
    } else {
      Object.values(InDirectAnswersId).forEach((questionId) => {
        apkSurvey[questionId] = answersModel.some((answer) => answer.answerUuid === questionId);
      });
    }

    return apkSurvey;
  }

  //pseudo constructor chain (cant overload constructor xd)
  public static buildSurveyFromOldAnswerModel(answerModel: OldAnswerModel, isDirectSurvey: boolean): ApkSurvey {
    const answersModel: NewAnswerModel[] = [];
    //adding existing answers
    if (answerModel !== null) {
      answersModel.push(
        ...Object.entries(answerModel)
          .filter(([_, value]) => value === true || value === false)
          .map(([key, value]) => ({
            answerUuid: value === true
              ? (isDirectSurvey ? DirectAnswersId[key as keyof typeof DirectAnswersId] : InDirectAnswersId[key as keyof typeof InDirectAnswersId])
              : (isDirectSurvey ? DirectAnswersId[`${key}_No` as keyof typeof DirectAnswersId] : InDirectAnswersId[`${key}_No` as keyof typeof InDirectAnswersId])
          }))
      );
      //adding missing answers in old model using Sobol survey
      if (!isDirectSurvey) {
        answersModel.push(
          { answerUuid: InDirectAnswersId.externalApk_No },
          { answerUuid: InDirectAnswersId.clientConsentForAPK }
        );
      }
      //Direct is missing ClientConsentForAPk
      else {
        answersModel.push({ answerUuid: DirectAnswersId.externalApk_No });
      }
    }
    // response calculation.apk empty == external apk true
    else {
      const externalApkAnswerUuid = isDirectSurvey ? DirectAnswersId.externalApk : InDirectAnswersId.externalApk;
      answersModel.push({ answerUuid: externalApkAnswerUuid });
    }

    return new ApkSurvey(answersModel, isDirectSurvey);
  }

  //pseudo chain
  public static buildSurveyOnlyNnw(isDirectSurvey: boolean): ApkSurvey {
    const answersModel: NewAnswerModel[] = [];

    // Push the answer UUIDs for the three specific questions
    const externalApkAnswerUuid = isDirectSurvey ? DirectAnswersId.externalApk_No : InDirectAnswersId.externalApk_No;
    const clientConsentAnswerUUid = isDirectSurvey ? '' : InDirectAnswersId.clientConsentForAPK;
    const nnwAnswerUuid = isDirectSurvey ? DirectAnswersId.nnw : InDirectAnswersId.nnw;
    const ocAnswerUuid = isDirectSurvey ? DirectAnswersId.oc_No : InDirectAnswersId.oc_No;
    const propertyInsuranceAnswerUuid = isDirectSurvey ? DirectAnswersId.propertyInsurance_No : InDirectAnswersId.propertyInsurance_No;

    answersModel.push(
      { answerUuid: externalApkAnswerUuid },
      { answerUuid: clientConsentAnswerUUid },
      { answerUuid: nnwAnswerUuid },
      { answerUuid: ocAnswerUuid },
      { answerUuid: propertyInsuranceAnswerUuid }
    );

    return new ApkSurvey(answersModel, isDirectSurvey);
  }

  //pseudo chain
  public static buildSurveyApkExternal(isDirectSurvey: boolean): ApkSurvey {
    const answersModel: NewAnswerModel[] = [];

    // Push the answer UUIDs for external apk
    const externalApkUuid = isDirectSurvey ? DirectAnswersId.externalApk : InDirectAnswersId.externalApk;

    answersModel.push(
      { answerUuid: externalApkUuid }
    );

    return new ApkSurvey(answersModel, isDirectSurvey);
  }

  public static buildSurveyApkExternalNoClientConsentNo(): ApkSurvey {
    const answersModel: NewAnswerModel[] = [];

    // Push the answer UUIDs for external apk_no and clientconsent_no
    const externalApkNoUuid = InDirectAnswersId.externalApk_No;
    const clientConsentNoUuid = InDirectAnswersId.clientConsentForAPK_No;


    answersModel.push(
      { answerUuid: externalApkNoUuid },
      { answerUuid: clientConsentNoUuid }
    );

    return new ApkSurvey(answersModel, false);
  }

  public isQuestionAnswered(questionId: DirectAnswersId | InDirectAnswersId): boolean {
    return this._survey[questionId] ?? false;
  }

  public isDirectSurvey() {
    return this._isDirectSurvey;
  }

  public getAnswersUuids(): string[] {
    return Object.entries(this._survey)
      .filter(([_, value]) => value === true)
      .map(([key]) => key);
  }

  public getAnswerOldModelValue(answerKey: string): boolean | undefined {
    let answerUuid = '';
    let answerNoUUid = '';
    if (this._isDirectSurvey) {
      answerUuid = DirectAnswersId[answerKey as keyof typeof DirectAnswersId];
      answerNoUUid = DirectAnswersId[`${answerKey}_No` as keyof typeof DirectAnswersId];
    }
    else {
      answerUuid = InDirectAnswersId[answerKey as keyof typeof InDirectAnswersId];
      answerNoUUid = InDirectAnswersId[`${answerKey}_No` as keyof typeof InDirectAnswersId];
    }

    if (answerUuid in this._survey && this._survey[answerUuid]) {
      return true;
    } else if (answerNoUUid in this._survey && this._survey[answerNoUUid]) {
      return false;
    } else {
      return undefined;
    }
  }
}
